import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../../../core/services/auth/auth.service';
import {
  JwtDecodeService,
  TokenInfo,
} from '../../../shared/services/jwt-decode-service';
import { WelcomeImageComponent } from '../../../components/welcome-image/welcome-image.component';

@Component({
  selector: 'app-set-new-password',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    WelcomeImageComponent,
  ],
  templateUrl: './set-new-password.component.html',
  styleUrl: './set-new-password.component.scss',
})
export class SetNewPasswordComponent {
  setNewPasswordForm!: FormGroup;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  email: string | null = '';
  code: string | null = '';
  type: string | null = '';
  title: string | null = '';
  token: string | null = null;
  isValid: boolean | null = null;

  readonly FIRST_LOGIN_EXPIRY_MINUTES = 24 * 60;
  readonly RESET_PASSWORD_EXPIRY_MINUTES = 60;
  readonly DEFAULT_EXPIRY_MINUTES = 30;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private readonly jwtDecodeService: JwtDecodeService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.extractTokenFromUrl();
  }

  private extractTokenFromUrl(): void {
    this.token = this.route.snapshot.queryParamMap.get('token');

    if (!this.token) {
      this.router.navigate(['/auth/login']);
      return;
    }

    const tokenInfo = this.jwtDecodeService.getTokenInfo(this.token);

    if (!tokenInfo.isValid) {
      this.isValid = false;
      this.router.navigate(['/auth/login']);
      return;
    }

    if (tokenInfo.isExpired) {
      this.isValid = false;
      this.email = tokenInfo.email || null;
      this.code = tokenInfo.code || null;
      this.type = tokenInfo.type || null;
      this.setTitleByLinkType();
      return;
    }

    this.isValid = true;
    this.email = tokenInfo.email || null;
    this.code = tokenInfo.code || null;
    this.type = tokenInfo.type || null;

    this.setTitleByLinkType();
  }

  initForm() {
    this.setNewPasswordForm = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', [Validators.required, Validators.minLength(8)]],
      },
      { validators: this.passwordsMatchValidator }
    );
  }

  togglePasswordVisibility(type: 'password' | 'confirmPassword') {
    if (type === 'password') {
      this.showPassword = !this.showPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.setNewPasswordForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  private passwordsMatchValidator(formGroup: AbstractControl) {
    const password = formGroup.get('password')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;

    if (password && confirmPassword && password !== confirmPassword) {
      formGroup.get('confirmPassword')?.setErrors({ notMatching: true });
    } else {
      const errors = formGroup.get('confirmPassword')?.errors;
      if (errors) {
        delete errors['notMatching'];
        if (Object.keys(errors).length === 0) {
          formGroup.get('confirmPassword')?.setErrors(null);
        } else {
          formGroup.get('confirmPassword')?.setErrors(errors);
        }
      }
    }
    return null;
  }

  submit(e: Event) {
    e.preventDefault();
    if (this.setNewPasswordForm.valid) {
      this.handlePasswordSubmission();
    } else {
      this.setNewPasswordForm.markAllAsTouched();
    }
  }

  handlePasswordSubmission() {
    switch (this.type) {
      case 'firstLogin':
        this.authService
          .firstLogin({
            email: this.email!,
            password: this.setNewPasswordForm.get('password')?.value,
            confirmPassword:
              this.setNewPasswordForm.get('confirmPassword')?.value,
            oldPassword: `${this.code!.trim()}`,
          })
          .subscribe((res: any) => {
            if (res.isSuccess) {
              this.setNewPasswordForm.reset();
              this.router.navigate(['/auth/password-set/']);
            } else {
              this.setNewPasswordForm.reset();
            }
          });
        break;

      case 'reset':
        this.authService
          .resetPassword({
            code: this.code!,
            email: this.email!,
            password: this.setNewPasswordForm.get('password')?.value,
            confirmPassword:
              this.setNewPasswordForm.get('confirmPassword')?.value,
          })
          .subscribe((res: any) => {
            if (res.data.result) {
              this.setNewPasswordForm.reset();
              this.router.navigate(['/auth/reset-password/']);
            } else {
              this.setNewPasswordForm.reset();
            }
          });
        break;

      default:
        break;
    }
  }

  setTitleByLinkType() {
    this.type === 'firstLogin'
      ? (this.title = 'auth.setNewPassword.title')
      : (this.title = 'auth.setNewPassword.resetPassword');
  }
}
